import { useMemo, useState } from 'react';
import { MessageSquarePlus } from 'lucide-react';
import { MAX_REQUEST_NOTE_LENGTH, REQUEST_TYPE } from '@hotel-voice/shared';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { QuantityStepper } from '@/components/common/QuantityStepper';
import { ErrorState, EmptyState, ListSkeleton, SuccessState, UnavailableState } from '@/components/common/StateView';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useReadyGuest } from '@/contexts/GuestSessionContext';
import { useSubmitRequest } from '@/hooks/useSubmitRequest';
import { formatCurrency, formatServicePrice } from '@/lib/money';
import { cn } from '@/lib/utils';
import {
  canSubmitHousekeeping,
  selectedLines,
  selectionTotal,
  setServiceQuantity,
  toRequestItems,
  toggleService,
  type HousekeepingSelection,
} from './selection';

/**
 * Housekeeping: tick what you need, set how many, add a note, send.
 *
 * The list is the hotel's own catalogue (already filtered to enabled rows by
 * the server). "Can't find what you need?" is the same request with no lines
 * and a note — it lands in the same queue as everything else.
 */
export function HousekeepingPage() {
  const { services, servicesPhase, servicesError, reloadServices } = useReadyGuest();
  const { submitting, result, submit, reset } = useSubmitRequest(REQUEST_TYPE.HOUSEKEEPING);

  const [selection, setSelection] = useState<HousekeepingSelection>({});
  const [note, setNote] = useState('');

  const catalogue = useMemo(() => services?.housekeeping ?? [], [services]);
  const lines = useMemo(() => selectedLines(selection, catalogue), [selection, catalogue]);
  const total = selectionTotal(lines);
  const ready = canSubmitHousekeeping(lines, note);

  const handleSubmit = async () => {
    if (!ready || submitting) return;
    const ok = await submit({ items: toRequestItems(lines), note });
    if (ok) {
      setSelection({});
      setNote('');
    }
  };

  const body = () => {
    if (servicesPhase === 'loading' || servicesPhase === 'idle') return <ListSkeleton />;
    if (servicesPhase === 'error') {
      return <ErrorState message={servicesError ?? 'Could not load services.'} onRetry={reloadServices} />;
    }
    if (!services?.flags.houseKeepingService) return <UnavailableState service="Housekeeping" />;

    if (result) {
      return (
        <SuccessState
          title="Request sent"
          message={
            result.totalCharge > 0
              ? `Housekeeping is on its way. ${formatCurrency(result.totalCharge)} will be added to your room bill.`
              : 'Housekeeping is on its way.'
          }
        >
          <Button onClick={reset}>Make another request</Button>
        </SuccessState>
      );
    }

    return (
      <div className="flex flex-col gap-4 p-4">
        {catalogue.length === 0 ? (
          <EmptyState
            title="No services listed"
            message="Tell us what you need below and we will sort it out."
          />
        ) : (
          <Card className="gap-0 py-0">
            <ul className="divide-y">
              {catalogue.map((service) => {
                const quantity = selection[service.id] ?? 0;
                const checked = quantity > 0;
                const inputId = `hk-${service.id}`;
                return (
                  <li key={service.id} className={cn('flex items-center gap-3 px-4 py-3', checked && 'bg-primary/5')}>
                    <Checkbox
                      id={inputId}
                      checked={checked}
                      onCheckedChange={() => setSelection((s) => toggleService(s, service.id))}
                      disabled={submitting}
                    />
                    <Label htmlFor={inputId} className="flex min-w-0 flex-1 cursor-pointer flex-col items-start gap-1">
                      <span className="text-sm font-medium">{service.name}</span>
                      <Badge variant={service.type === 'paid' ? 'default' : 'success'}>
                        {formatServicePrice(service)}
                      </Badge>
                    </Label>
                    {checked && (
                      <QuantityStepper
                        label={service.name}
                        value={quantity}
                        onChange={(next) => setSelection((s) => setServiceQuantity(s, service.id, next))}
                        disabled={submitting}
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          </Card>
        )}

        <Card>
          <CardContent className="flex flex-col gap-2">
            <Label htmlFor="hk-note" className="gap-2 text-base font-semibold">
              <MessageSquarePlus className="text-primary size-4" />
              Can’t find what you need?
            </Label>
            <p className="text-muted-foreground text-xs">
              Describe it here, or add a note to the services you picked above.
            </p>
            <Textarea
              id="hk-note"
              placeholder="e.g. Please provide a hair dryer"
              value={note}
              maxLength={MAX_REQUEST_NOTE_LENGTH}
              onChange={(event) => setNote(event.target.value)}
              disabled={submitting}
            />
          </CardContent>
        </Card>

        {lines.length > 0 && (
          <Card>
            <CardContent className="flex flex-col gap-2 text-sm">
              <p className="font-semibold">Your request</p>
              {lines.map((line) => (
                <div key={line.service.id} className="flex justify-between gap-3">
                  <span className="min-w-0 truncate">
                    {line.service.name}
                    {line.quantity > 1 && <span className="text-muted-foreground"> ×{line.quantity}</span>}
                  </span>
                  <span className="tabular-nums">
                    {line.lineTotal > 0 ? formatCurrency(line.lineTotal) : 'Free'}
                  </span>
                </div>
              ))}
              {total > 0 && (
                <>
                  <Separator className="my-1" />
                  <div className="flex justify-between font-semibold">
                    <span>Total charge</span>
                    <span className="tabular-nums">{formatCurrency(total)}</span>
                  </div>
                  <p className="text-muted-foreground text-xs">Added to your room bill.</p>
                </>
              )}
            </CardContent>
          </Card>
        )}

        <Button size="lg" onClick={handleSubmit} disabled={!ready || submitting} aria-busy={submitting}>
          {submitting ? 'Sending…' : total > 0 ? `Request · ${formatCurrency(total)}` : 'Send request'}
        </Button>
      </div>
    );
  };

  return (
    <AppShell>
      <PageHeader title="Housekeeping" subtitle="Extra amenities and room care" />
      {body()}
    </AppShell>
  );
}
